"use client";
import { use, useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  saveSearchPayload,
  getSearchPayload,
  getSearchLoading,
  loading,
  getHotels,
  setHotelAppliedFilters,
} from "@/lib/features/hotel/hotelSlice";

import DateSearch from "@/components/hero/hero-1/DateSearch";
import GuestSearch from "@/components/hero/hero-1/GuestSearch";
import LocationSearch from "@/components/hero/hero-1/LocationSearch";
import { useRouter, useSearchParams } from "next/navigation";
import { DateObject } from "react-multi-date-picker";
import {
  getUserCurrency,
  getUserCurrentCountry,
} from "@/lib/features/home/homeSlice";
import { HomeSearchSchema } from "@/utils/validation";
import { Formik, Form, Field, useFormikContext } from "formik";
import { usePathname } from "next/navigation";
import { extractHotelCode, transformGuests } from "@/utils/helper";
import { makeQueryString, parseQueryString } from "@/utils/queryString";
interface Room {
  adults: number;
  children_ages: (number | null)[];
  no_of_infants?: number | undefined;
}

interface Location {
  name: string;
  code: string;
  type: string;
  label: string;
}
interface FormValues {
  location: Location;
  dates: string[];
  guests: Room[];
  [key: string]: any;
}
type MainFilterSearchBoxProps = {
  requestFrom?: string;
  onSearch?: () => any;
};
import { useDebounce } from "use-debounce";
import useMediaQuery from "@/hooks/useMediaQuery";
import React from "react";
import { SEARCH_HOTEL_EVENT, trackEvent } from "@/utils/gtm";

const MainFilterSearchBox = ({
  requestFrom,
  onSearch,
}: MainFilterSearchBoxProps) => {
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const router = useRouter();
  const dispatch = useAppDispatch();
  const currentPage = usePathname();

  // Split the pathname and extract segments
  const segments = currentPage?.split("/").filter(Boolean); // Removes empty strings
  const city = segments[1]; // "Cancun"
  const hotelCode = segments[2]; // "Breathless+Riviera+Cancun+Resort+&+Spa-1596190"
  const data: any = extractHotelCode(hotelCode);

  // console.log("currentPage", currentPage)
  const query: any = useSearchParams();
  //   const decodedQueryParams = query?.get("payload")
  //     ? JSON.parse(query.get("payload"))
  //     : {};

  //   const qParams: any = query?.get("q") ? query?.get("q") : {};
  const userCurrency = useAppSelector(getUserCurrency);
  const searchPayload = useAppSelector(getSearchPayload);
  const searchLoading = useAppSelector(getSearchLoading);
  const hotelLoading = useAppSelector(loading);
  const userCurrentCountry = useAppSelector(getUserCurrentCountry);
  //   const [debouncedQuery] = useDebounce(decodedQueryParams, 500);
  //   const [debouncedQQuery] = useDebounce(qParams, 500);
  // Parse the query string into a payload using parseQueryString
  const payloadFromQuery: any = useMemo(() => {
    if (!query) return null; // Handle case where searchParams isn't ready
    return parseQueryString(query);
  }, [query]);

  const initialValues: FormValues = useMemo(
    () => ({
      location: {
        name: data?.name || "",
        code: data?.hotel_code || "",
        type: "city",
        label: `${city ?? ""}, ${data?.name ?? ""}`,
      },
      dates: [
        new DateObject().format("YYYY-MM-DD"),
        new DateObject().add(2, "days").format("YYYY-MM-DD"),
      ],
      guests: [{ adults: 2, children_ages: [] }],
    }),
    [data, city]
  );

  const onSubmit = useCallback(
    async (values: any) => {
      const payload = {
        ...values,
        currency: userCurrency?.code,
        nationality: userCurrency?.nationality_code,
        time: Math.floor(Date.now() / 1000),
      };

      const queryString = makeQueryString(payload);
      const saved = await dispatch(saveSearchPayload(payload)).unwrap();

      if (saved) {
        if (requestFrom === "home") {
          dispatch(
            setHotelAppliedFilters({
              hotel_code: values?.location?.code,
              filters: [],
            })
          );
        }

        if (requestFrom === "hotels") onSearch?.();

        trackEvent(SEARCH_HOTEL_EVENT, payload);
        router.push(`/hotels?${queryString}`);
      }
    },
    [dispatch, router, userCurrency, requestFrom, onSearch]
  );

  const getSearchUiClasses = useCallback(() => {
    let element;
    if (currentPage == "/hotels") {
      element =
        "mainSearch -col-3-big bg-white px-10 py-10 lg:px-20 lg:pt-5 lg:pb-20 borderradius-40 mt-30";
    }
    //Sets the search box to the same width as the main content
    else if (
      currentPage.startsWith("/hotels/") ||
      currentPage.startsWith("/hotel")
    ) {
      element =
        "mainSearch bg-white px-10 py-10 lg:px-20 lg:pt-5 lg:pb-20 rounded-100";
    } else {
      element =
        "mainSearch -w-900 bg-white px-10 py-10 lg:px-20 lg:pt-5 lg:pb-20 borderradius-40";
    }
    return element;
  }, [currentPage]);

  const SearchPayloadSync = ({
    payloadFromQuery,
    initialValues,
    requestFrom,
    data,
    city,
  }: any) => {
    const { setFieldValue } = useFormikContext<any>();

    useEffect(() => {
      if (
        requestFrom === "hotels" &&
        (payloadFromQuery?.location?.code || data?.name)
      ) {
        const source = payloadFromQuery?.location?.name
          ? payloadFromQuery
          : {
              ...initialValues,
              location: {
                name: data?.name || "",
                code: data?.hotel_code || "",
                type: "city",
                label: `${city ?? ""}, ${data?.name ?? ""}`,
              },
            };

        const location = source?.location;
        const guests = source?.guests ?? initialValues?.guests;
        const dates = source?.dates ?? initialValues?.dates;

        setFieldValue("location", location);
        setFieldValue("dates", dates);
        setFieldValue("guests", guests);
      }
    }, [
      payloadFromQuery,
      requestFrom,
      data,
      city,
      initialValues,
      setFieldValue,
    ]);

    return null;
  };

  return (
    <>
      {/* initialValues{JSON.stringify(data)} */}

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={HomeSearchSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => {
          
          return (
            <Form>
              {/* ✅ Hook-safe payload sync */}
              <SearchPayloadSync
                payloadFromQuery={payloadFromQuery}
                initialValues={initialValues}
                requestFrom={requestFrom}
                data={data}
                city={city}
              />

              <div className={getSearchUiClasses()}>
                <div className="button-grid items-center">
                  {/* {JSON.stringify(values?.guests)} */}
                  <LocationSearch
                    name="location"
                    value={values?.location}
                    setFieldValue={setFieldValue}
                  />

                  <div className="searchMenu-date px-30 sm:py-10 lg:py-20 lg:px-0 js-form-dd js-calendar">
                    <div className={isDesktop == false ? "pt-20" : ""}>
                      <h4 className="text-15 fw-500 ls-2 lh-16">
                        Check in - Check out
                      </h4>
                      <Field name="dates" id="dates">
                        {({ field }: any) => (
                          <DateSearch
                            name="dates"
                            value={values?.dates}
                            setFieldValue={setFieldValue}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className={isDesktop == false ? "pt-20" : ""}>
                    <GuestSearch
                      name="guests"
                      guests={values?.guests}
                      setFieldValue={setFieldValue}
                    />
                  </div>
                  <div className="button-item">
                    <button
                      // disabled={hotelLoading}
                      type="submit"
                      className="mainSearch__submit button -theme-blue-color bg-theme-brown-color h-60 px-35 col-12 borderradius-40  text-white"
                    >
                      <i className="icon-search text-20 mr-10" />
                      {searchLoading ? "Finding deals..." : "Find deals"}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default MainFilterSearchBox;
